import React, { useState } from 'react';
import { 
  Sliders, MessageSquare, Clock, Target, 
  ChevronRight, ChevronDown, Plus, Edit2, Trash2,
  Check, X, AlertCircle 
} from 'lucide-react';

const RoutingRules = ({ onSuccess }) => {
  // State for add rule interface
  const [showAddRuleSection, setShowAddRuleSection] = useState(false);
  const [activeTab, setActiveTab] = useState('confidence');
  const [confidenceThreshold, setConfidenceThreshold] = useState(75);
  
  // State for standard rule form
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [ruleCondition, setRuleCondition] = useState('');
  const [ruleAssignment, setRuleAssignment] = useState('');
  
  // State for natural language
  const [naturalLanguageInput, setNaturalLanguageInput] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [interpretedRule, setInterpretedRule] = useState(null);
  
  // State for accordion sections
  const [expandedSections, setExpandedSections] = useState({
    language: true,
    schedule: false,
    source: false
  });
  
  // State for editing
  const [editingRule, setEditingRule] = useState(null);
  const [editForm, setEditForm] = useState({
    name: '',
    description: '',
    conditions: []
  });
  
  // Sample routing rules data
  const [routingRules, setRoutingRules] = useState({
    language: [
      {
        id: 'lang-1',
        name: 'Spanish Speaker Priority',
        description: 'Route Spanish-speaking customers to bilingual team members',
        conditions: ['Language: Spanish', 'Priority: High'],
        assignedTo: ['Maria Rodriguez', 'Carlos Santos']
      },
      {
        id: 'lang-2',
        name: 'Russian Language Support',
        description: 'Route Russian speakers to Vlad and Erik',
        conditions: ['Language: Russian'],
        assignedTo: ['Vlad Petrov', 'Erik Ivanov']
      }
    ],
    schedule: [
      {
        id: 'sched-1',
        name: 'After Hours Routing',
        description: 'Route leads received after 6 PM to next-day priority queue',
        conditions: ['Time: After 6:00 PM', 'Action: Queue for next day'],
        assignedTo: ['Morning Shift Team']
      },
      {
        id: 'sched-2',
        name: 'Weekend Lead Distribution',
        description: 'Distribute weekend leads evenly across on-call staff',
        conditions: ['Day: Saturday/Sunday', 'Distribution: Round-robin'],
        assignedTo: ['On-Call Team']
      }
    ],
    source: [
      {
        id: 'source-1',
        name: 'Website Lead Fast Track',
        description: 'High-value website leads get immediate assignment to top performers',
        conditions: ['Source: Website', 'Value: High', 'Response: Immediate'],
        assignedTo: ['Top Performers']
      },
      {
        id: 'source-2',
        name: 'Trade-In Leads to Service',
        description: 'Trade-in leads route to service department for inspection',
        conditions: ['Type: Trade-in', 'Department: Service'],
        assignedTo: ['Service Department']
      }
    ]
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleConfidenceSlider = () => {
    onSuccess(`Confidence threshold updated to ${confidenceThreshold}%`);
    setShowAddRuleSection(false);
  };

  const handleStandardRule = () => {
    if (!selectedTemplate || !ruleCondition || !ruleAssignment) {
      alert('Please fill in all fields');
      return;
    }
    
    onSuccess(`New ${selectedTemplate} rule created: "${ruleCondition}" → ${ruleAssignment}`);
    
    setSelectedTemplate('');
    setRuleCondition('');
    setRuleAssignment('');
    setShowAddRuleSection(false);
  };

  const handleNaturalLanguage = () => {
    if (!naturalLanguageInput.trim()) {
      alert('Please enter a rule description');
      return;
    }

    const interpreted = interpretNaturalLanguage(naturalLanguageInput);
    setInterpretedRule(interpreted);
    setShowConfirmation(true);
  };

  const interpretNaturalLanguage = (input) => {
    const lowerInput = input.toLowerCase();
    
    if (lowerInput.includes('russian') && (lowerInput.includes('vlad') || lowerInput.includes('erik'))) {
      return {
        type: 'language',
        name: 'Russian Language Support',
        description: 'Route Russian speakers to Vlad and Erik',
        conditions: ['Language: Russian'],
        assignedTo: ['Vlad Petrov', 'Erik Ivanov']
      };
    } else if (lowerInput.includes('avo') && lowerInput.includes('trade')) {
      return {
        type: 'source',
        name: 'AVO Form - Trade-In Processing',
        description: 'Fill out AVO with trade information from vAuto and send to Jenny',
        conditions: ['Type: Trade-in', 'Action: AVO Form', 'Data Source: vAuto'],
        assignedTo: ['Jenny (Service)']
      };
    } else if (lowerInput.includes('spanish')) {
      return {
        type: 'language',
        name: 'Spanish Speaker Routing',
        description: input,
        conditions: ['Language: Spanish'],
        assignedTo: ['Bilingual Team']
      };
    } else {
      return {
        type: 'source',
        name: 'Custom Routing Rule',
        description: input,
        conditions: ['Custom Rule'],
        assignedTo: ['To Be Assigned']
      };
    }
  };

  const confirmNaturalLanguageRule = () => {
    if (!interpretedRule) return;

    const category = interpretedRule.type;
    const newRule = {
      id: `${category}-${Date.now()}`,
      ...interpretedRule
    };

    setRoutingRules(prev => ({
      ...prev,
      [category]: [...prev[category], newRule]
    }));

    onSuccess(`New rule created: "${interpretedRule.name}"`);
    
    setNaturalLanguageInput('');
    setShowConfirmation(false);
    setInterpretedRule(null);
    setShowAddRuleSection(false);
  };

  const cancelNaturalLanguageRule = () => {
    setShowConfirmation(false);
    setInterpretedRule(null);
  };

  const startEditRule = (ruleId, category) => {
    const rule = routingRules[category].find(r => r.id === ruleId);
    if (rule) {
      setEditingRule({ id: ruleId, category });
      setEditForm({
        name: rule.name,
        description: rule.description,
        conditions: [...rule.conditions]
      });
    }
  };

  const saveEditedRule = () => {
    if (!editingRule) return;

    const { id, category } = editingRule;
    
    setRoutingRules(prev => ({
      ...prev,
      [category]: prev[category].map(rule => 
        rule.id === id 
          ? { ...rule, ...editForm }
          : rule
      )
    }));

    onSuccess(`Rule "${editForm.name}" updated successfully`);
    setEditingRule(null);
    setEditForm({ name: '', description: '', conditions: [] });
  };

  const cancelEdit = () => {
    setEditingRule(null);
    setEditForm({ name: '', description: '', conditions: [] });
  };

  const deleteRule = (ruleId, category) => {
    if (window.confirm('Are you sure you want to delete this rule?')) {
      const ruleName = routingRules[category].find(r => r.id === ruleId)?.name;
      
      setRoutingRules(prev => ({
        ...prev,
        [category]: prev[category].filter(r => r.id !== ruleId)
      }));

      onSuccess(`Rule "${ruleName}" deleted`);
    }
  };

  const standardTemplates = [
    { value: 'language', label: 'Language-Based Routing' },
    { value: 'schedule', label: 'Time/Schedule Based' },
    { value: 'source', label: 'Lead Source Routing' },
    { value: 'value', label: 'Customer Value Tier' },
    { value: 'geography', label: 'Geographic Territory' },
    { value: 'product', label: 'Product/Service Type' }
  ];

  return (
    <>
      <div className="header">
        <h1>Routing Rules</h1>
        <p>Configure AI routing logic and team assignments</p>
      </div>

      <button 
        className="add-rule-btn"
        onClick={() => setShowAddRuleSection(!showAddRuleSection)}
        style={{
          background: showAddRuleSection 
            ? 'rgba(251, 191, 36, 0.1)' 
            : 'linear-gradient(135deg, #00D4AA 0%, #00A88A 100%)',
          border: showAddRuleSection 
            ? '2px solid rgba(251, 191, 36, 0.3)' 
            : '2px solid rgba(0, 212, 170, 0.3)',
          borderRadius: '12px',
          padding: '14px 24px',
          color: '#FFFFFF',
          fontSize: '15px',
          fontWeight: '600',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          marginBottom: '24px',
          transition: 'all 0.3s ease'
        }}
      >
        {showAddRuleSection ? (
          <>
            <X size={18} />
            Cancel
          </>
        ) : (
          <>
            <Plus size={18} />
            Add New Rule
          </>
        )}
      </button>

      {showAddRuleSection && (
        <div style={{
          background: 'linear-gradient(135deg, #0f1d35 0%, #0d1829 100%)',
          border: '2px solid rgba(0, 212, 170, 0.2)',
          borderRadius: '16px',
          padding: '32px',
          marginBottom: '32px'
        }}>
          <div style={{ display: 'flex', gap: '12px', marginBottom: '32px', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
            {[
              { id: 'confidence', label: 'Confidence Threshold', icon: <Sliders size={18} /> },
              { id: 'standard', label: 'Standard Rules', icon: <Target size={18} /> },
              { id: 'natural', label: 'Natural Language', icon: <MessageSquare size={18} /> }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  background: activeTab === tab.id ? 'rgba(0, 212, 170, 0.1)' : 'transparent',
                  border: 'none',
                  borderBottom: activeTab === tab.id ? '3px solid #00D4AA' : '3px solid transparent',
                  color: activeTab === tab.id ? '#00D4AA' : 'rgba(255, 255, 255, 0.6)',
                  padding: '12px 20px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.3s ease'
                }}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </div>

          {activeTab === 'confidence' && (
            <div>
              <label style={{ display: 'block', color: 'rgba(255, 255, 255, 0.9)', fontSize: '15px', fontWeight: '600', marginBottom: '16px' }}>
                Minimum Confidence Score
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                <input 
                  type="range"
                  min="50"
                  max="95"
                  value={confidenceThreshold}
                  onChange={(e) => setConfidenceThreshold(parseInt(e.target.value))}
                  className="confidence-slider"
                  style={{
                    flex: 1,
                    height: '8px',
                    background: `linear-gradient(to right, #00D4AA 0%, #00D4AA ${((confidenceThreshold - 50) / 45) * 100}%, rgba(255, 255, 255, 0.1) ${((confidenceThreshold - 50) / 45) * 100}%, rgba(255, 255, 255, 0.1) 100%)`,
                    borderRadius: '4px',
                    outline: 'none',
                    cursor: 'pointer'
                  }}
                />
                <div style={{
                  background: 'rgba(0, 212, 170, 0.1)',
                  border: '2px solid #00D4AA',
                  borderRadius: '8px',
                  padding: '8px 16px',
                  color: '#00D4AA',
                  fontSize: '18px',
                  fontWeight: '700',
                  minWidth: '70px',
                  textAlign: 'center'
                }}>
                  {confidenceThreshold}%
                </div>
              </div>
              <p style={{ marginTop: '12px', fontSize: '13px', color: 'rgba(255, 255, 255, 0.5)' }}>
                Leads with AI confidence below this threshold will be flagged for manager review
              </p>
              <button
                onClick={handleConfidenceSlider}
                style={{
                  marginTop: '24px',
                  background: 'linear-gradient(135deg, #00D4AA 0%, #00A88A 100%)',
                  border: '2px solid rgba(0, 212, 170, 0.3)',
                  borderRadius: '10px',
                  padding: '12px 24px',
                  color: '#FFFFFF',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Save Threshold
              </button>
            </div>
          )}

          {activeTab === 'standard' && (
            <div>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', color: 'rgba(255, 255, 255, 0.9)', fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>
                  Rule Template
                </label>
                <select
                  value={selectedTemplate}
                  onChange={(e) => setSelectedTemplate(e.target.value)}
                  style={{
                    width: '100%',
                    background: '#1a2332',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '8px',
                    padding: '12px 16px',
                    color: '#FFFFFF',
                    fontSize: '14px'
                  }}
                >
                  <option value="">Select a template...</option>
                  {standardTemplates.map(template => (
                    <option key={template.value} value={template.value}>
                      {template.label}
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', color: 'rgba(255, 255, 255, 0.9)', fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>
                  Condition
                </label>
                <input
                  type="text"
                  value={ruleCondition}
                  onChange={(e) => setRuleCondition(e.target.value)}
                  placeholder="e.g., Language: Spanish"
                  style={{
                    width: '100%',
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '8px',
                    padding: '12px 16px',
                    color: '#FFFFFF',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
  <label style={{ display: 'block', color: 'rgba(255, 255, 255, 0.9)', fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>
    Assign To
  </label>
  <select
    value={ruleAssignment}
    onChange={(e) => setRuleAssignment(e.target.value)}
    style={{
      width: '100%',
      background: '#1a2332',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      borderRadius: '8px',
      padding: '12px 16px',
      color: '#FFFFFF',
      fontSize: '14px'
    }}
  >
    <option value="">Select a team member...</option>
    <option value="Maria Rodriguez">Maria Rodriguez</option>
    <option value="Carlos Santos">Carlos Santos</option>
    <option value="Vlad Petrov">Vlad Petrov</option>
    <option value="Erik Ivanov">Erik Ivanov</option>
    <option value="Jenny (Service)">Jenny (Service)</option>
    <option value="Sarah Johnson">Sarah Johnson</option>
    <option value="Mike Chen">Mike Chen</option>
    <option value="Top Performers">Top Performers (Group)</option>
    <option value="Bilingual Team">Bilingual Team (Group)</option>
    <option value="Service Department">Service Department (Group)</option>
  </select>
</div>

              <button
                onClick={handleStandardRule}
                style={{
                  background: 'linear-gradient(135deg, #00D4AA 0%, #00A88A 100%)',
                  border: '2px solid rgba(0, 212, 170, 0.3)',
                  borderRadius: '10px',
                  padding: '12px 24px',
                  color: '#FFFFFF',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Create Rule
              </button>
            </div>
          )}

          {activeTab === 'natural' && !showConfirmation && (
            <div>
              <label style={{ display: 'block', color: 'rgba(255, 255, 255, 0.9)', fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>
                Describe the routing rule in plain English
              </label>
              <textarea
                value={naturalLanguageInput}
                onChange={(e) => setNaturalLanguageInput(e.target.value)}
                placeholder="e.g., 'Make a routing rule for Russian speakers and add Vlad and Erik' or 'After the sale, fill out an AVO with the trade information from vAuto and send it to Jenny'"
                style={{
                  width: '100%',
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '8px',
                  padding: '16px',
                  color: '#FFFFFF',
                  fontSize: '14px',
                  minHeight: '120px',
                  resize: 'vertical'
                }}
              />
              <p style={{ marginTop: '8px', fontSize: '13px', color: 'rgba(255, 255, 255, 0.5)' }}>
                The AI will interpret your description and show you the rule for confirmation
              </p>
              <button
                onClick={handleNaturalLanguage}
                style={{
                  marginTop: '16px',
                  background: 'linear-gradient(135deg, #00D4AA 0%, #00A88A 100%)',
                  border: '2px solid rgba(0, 212, 170, 0.3)',
                  borderRadius: '10px',
                  padding: '12px 24px',
                  color: '#FFFFFF',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Process Rule
              </button>
            </div>
          )}

          {activeTab === 'natural' && showConfirmation && interpretedRule && (
            <div>
              <div style={{
                background: 'rgba(0, 212, 170, 0.05)',
                border: '2px solid rgba(0, 212, 170, 0.3)',
                borderRadius: '12px',
                padding: '20px',
                marginBottom: '20px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                  <AlertCircle size={20} color="#00D4AA" />
                  <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#00D4AA', margin: 0 }}>
                    Confirm Rule Interpretation
                  </h3>
                </div>
                
                <div style={{ marginBottom: '12px' }}>
                  <div style={{ fontSize: '13px', color: 'rgba(255, 255, 255, 0.6)', marginBottom: '4px' }}>Rule Name:</div>
                  <div style={{ fontSize: '15px', fontWeight: '600', color: '#FFFFFF' }}>{interpretedRule.name}</div>
                </div>

                <div style={{ marginBottom: '12px' }}>
                  <div style={{ fontSize: '13px', color: 'rgba(255, 255, 255, 0.6)', marginBottom: '4px' }}>Description:</div>
                  <div style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.9)' }}>{interpretedRule.description}</div>
                </div>

                <div style={{ marginBottom: '12px' }}>
                  <div style={{ fontSize: '13px', color: 'rgba(255, 255, 255, 0.6)', marginBottom: '8px' }}>Conditions:</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {interpretedRule.conditions.map((condition, idx) => (
                      <span 
                        key={idx}
                        style={{
                          background: 'rgba(0, 212, 170, 0.1)',
                          border: '1px solid rgba(0, 212, 170, 0.3)',
                          borderRadius: '6px',
                          padding: '6px 12px',
                          fontSize: '13px',
                          color: '#00D4AA'
                        }}
                      >
                        {condition}
                      </span>
                    ))}
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: '13px', color: 'rgba(255, 255, 255, 0.6)', marginBottom: '8px' }}>Assigned To:</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {interpretedRule.assignedTo.map((person, idx) => (
                      <span 
                        key={idx}
                        style={{
                          background: 'rgba(251, 191, 36, 0.1)',
                          border: '1px solid rgba(251, 191, 36, 0.3)',
                          borderRadius: '6px',
                          padding: '6px 12px',
                          fontSize: '13px',
                          color: '#fbbf24'
                        }}
                      >
                        {person}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  onClick={confirmNaturalLanguageRule}
                  style={{
                    flex: 1,
                    background: 'linear-gradient(135deg, #00D4AA 0%, #00A88A 100%)',
                    border: '2px solid rgba(0, 212, 170, 0.3)',
                    borderRadius: '10px',
                    padding: '12px 24px',
                    color: '#FFFFFF',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                >
                  <Check size={18} />
                  Confirm & Create Rule
                </button>
                <button
                  onClick={cancelNaturalLanguageRule}
                  style={{
                    flex: 1,
                    background: 'rgba(239, 68, 68, 0.1)',
                    border: '2px solid rgba(239, 68, 68, 0.3)',
                    borderRadius: '10px',
                    padding: '12px 24px',
                    color: '#ef4444',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                >
                  <X size={18} />
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {['language', 'schedule', 'source'].map(category => (
          <div key={category} style={{
            background: 'linear-gradient(135deg, #0f1d35 0%, #0d1829 100%)',
            border: '1px solid rgba(0, 212, 170, 0.1)',
            borderRadius: '16px',
            overflow: 'hidden',
            transition: 'all 0.3s ease'
          }}>
            <div 
              onClick={() => toggleSection(category)}
              style={{
                padding: '20px 24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                background: expandedSections[category] ? 'rgba(0, 212, 170, 0.05)' : 'transparent'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                {category === 'language' && <MessageSquare size={20} color="#00D4AA" />}
                {category === 'schedule' && <Clock size={20} color="#00D4AA" />}
                {category === 'source' && <Target size={20} color="#00D4AA" />}
                <span style={{ fontSize: '16px', fontWeight: '700', color: '#FFFFFF' }}>
                  {category === 'language' && 'Language Routing'}
                  {category === 'schedule' && 'Schedule & Availability'}
                  {category === 'source' && 'Lead Source'}
                </span>
                <span style={{
                  background: 'rgba(0, 212, 170, 0.1)',
                  border: '1px solid rgba(0, 212, 170, 0.3)',
                  borderRadius: '12px',
                  padding: '4px 10px',
                  fontSize: '12px',
                  fontWeight: '600',
                  color: '#00D4AA'
                }}>
                  {routingRules[category].length}
                </span>
              </div>
              {expandedSections[category] ? (
                <ChevronDown size={20} color="rgba(255, 255, 255, 0.5)" />
              ) : (
                <ChevronRight size={20} color="rgba(255, 255, 255, 0.5)" />
              )}
            </div>
            
            {expandedSections[category] && (
              <div style={{ padding: '0 24px 24px' }}>
                {routingRules[category].map(rule => (
                  <div 
                    key={rule.id}
                    style={{
                      bbackground: 'rgba(0, 0, 0, 0.3)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '12px',
                      padding: '16px',
                      marginBottom: '12px'
                    }}
                  >
                    {editingRule?.id === rule.id ? (
                      <div>
                        <input
                          type="text"
                          value={editForm.name}
                          onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                          style={{
                            width: '100%',
                            background: 'rgba(255, 255, 255, 0.05)',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            borderRadius: '8px',
                            padding: '10px 14px',
                            color: '#FFFFFF',
                            fontSize: '15px',
                            fontWeight: '600',
                            marginBottom: '12px'
                          }}
                        />
                        <textarea
                          value={editForm.description}
                          onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                          style={{
                            width: '100%',
                            background: 'rgba(255, 255, 255, 0.05)',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            borderRadius: '8px',
                            padding: '10px 14px',
                            color: 'rgba(255, 255, 255, 0.8)',
                            fontSize: '13px',
                            marginBottom: '12px',
                            minHeight: '60px',
                            resize: 'vertical'
                          }}
                        />
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            onClick={saveEditedRule}
                            style={{
                              background: 'linear-gradient(135deg, #00D4AA 0%, #00A88A 100%)',
                              border: 'none',
                              borderRadius: '8px',
                              padding: '8px 16px',
                              color: '#FFFFFF',
                              fontSize: '13px',
                              fontWeight: '600',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px'
                            }}
                          >
                            <Check size={14} />
                            Save
                          </button>
                          <button
                            onClick={cancelEdit}
                            style={{
                              background: 'rgba(255, 255, 255, 0.05)',
                              border: '1px solid rgba(255, 255, 255, 0.1)',
                              borderRadius: '8px',
                              padding: '8px 16px',
                              color: 'rgba(255, 255, 255, 0.7)',
                              fontSize: '13px',
                              fontWeight: '600',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px'
                            }}
                          >
                            <X size={14} />
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div style={{ display: 'flex', alignItems: 'start', justifyContent: 'space-between', marginBottom: '8px' }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: '15px', fontWeight: '700', color: '#FFFFFF', marginBottom: '6px' }}>
                              {rule.name}
                            </div>
                            <div style={{ fontSize: '13px', color: 'rgba(255, 255, 255, 0.6)', marginBottom: '12px' }}>
                              {rule.description}
                            </div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '8px' }}>
                              {rule.conditions.map((condition, idx) => (
                                <span 
                                  key={idx}
                                  style={{
                                    background: 'rgba(0, 212, 170, 0.1)',
                                    border: '1px solid rgba(0, 212, 170, 0.2)',
                                    borderRadius: '6px',
                                    padding: '4px 10px',
                                    fontSize: '12px',
                                    color: '#00D4AA'
                                  }}
                                >
                                  {condition}
                                </span>
                              ))}
                            </div>
                            <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.5)' }}>
                              Assigned to: {rule.assignedTo.join(', ')}
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button
                              onClick={() => startEditRule(rule.id, category)}
                              style={{
                                background: 'rgba(0, 212, 170, 0.1)',
                                border: '1px solid rgba(0, 212, 170, 0.3)',
                                borderRadius: '8px',
                                padding: '8px',
                                color: '#00D4AA',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center'
                              }}
                              title="Edit rule"
                            >
                              <Edit2 size={14} />
                            </button>
                            <button
                              onClick={() => deleteRule(rule.id, category)}
                              style={{
                                background: 'rgba(239, 68, 68, 0.1)',
                                border: '1px solid rgba(239, 68, 68, 0.3)',
                                borderRadius: '8px',
                                padding: '8px',
                                color: '#ef4444',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center'
                              }}
                              title="Delete rule"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </>
  );
};

export default RoutingRules;